import { Boxes, Package, Layers, Ruler, Paintbrush, HardHat, Droplet, Zap, Wrench, Trees, Hammer, Trash2, Flame, Plug } from 'lucide-react';

// UK retailers. Skimlinks auto-converts supported ones into commission links at
// runtime — we just build normal deep links to each retailer's search results.
export const RETAILERS = [
  { name: 'Toolstation', kind: 'Trade', url: (q) => `https://www.toolstation.com/search?q=${encodeURIComponent(q)}` },
  { name: 'Travis Perkins', kind: 'Trade', url: (q) => `https://www.travisperkins.co.uk/search?q=${encodeURIComponent(q)}` },
  { name: 'Screwfix', kind: 'Trade', url: (q) => `https://www.screwfix.com/search?search=${encodeURIComponent(q)}` },
  { name: 'B&Q', kind: 'DIY', url: (q) => `https://www.diy.com/search?term=${encodeURIComponent(q)}` },
  { name: 'Wickes', kind: 'DIY', url: (q) => `https://www.wickes.co.uk/search?text=${encodeURIComponent(q)}` },
  { name: 'ManoMano', kind: 'DIY', url: (q) => `https://www.manomano.co.uk/search/${encodeURIComponent(q)}` },
  { name: 'Amazon', kind: 'DIY', url: (q) => `https://www.amazon.co.uk/s?k=${encodeURIComponent(q)}` },
];

// Shop page categories (mix of materials + tools). Each links to every retailer's
// search for that term.
export const SHOP_CATEGORIES = [
  { title: 'Bricks & Blocks', q: 'facing bricks', icon: Boxes },
  { title: 'Cement, Sand & Aggregates', q: 'cement', icon: Package },
  { title: 'Concrete & Mortar', q: 'ready mix concrete', icon: Layers },
  { title: 'Timber & Sheet Materials', q: 'timber', icon: Ruler },
  { title: 'Plaster & Plasterboard', q: 'plasterboard', icon: Layers },
  { title: 'Insulation', q: 'loft insulation', icon: Package },
  { title: 'Paint & Decorating', q: 'paint', icon: Paintbrush },
  { title: 'Tiles & Adhesive', q: 'wall tiles', icon: Layers },
  { title: 'Roofing', q: 'roof tiles', icon: HardHat },
  { title: 'Plumbing & Heating', q: 'copper pipe', icon: Droplet },
  { title: 'Radiators & Boilers', q: 'radiators', icon: Flame },
  { title: 'Electrical', q: 'electrical cable', icon: Zap },
  { title: 'Fixings & Screws', q: 'wood screws', icon: Wrench },
  { title: 'Landscaping & Paving', q: 'paving slabs', icon: Trees },
  { title: 'Hand & Power Tools', q: 'power tools', icon: Hammer },
  { title: 'PPE & Workwear', q: 'workwear', icon: HardHat },
  { title: 'Fencing & Decking', q: 'fence panels', icon: Trees },
  { title: 'Skips & Waste', q: 'skip bags', icon: Trash2 },
];

// Per-trade fallback keywords for calculator "Where to buy" blocks.
const TRADE_KEYWORDS = {
  'bricklayers': [{ label: 'Bricks', q: 'facing bricks' }, { label: 'Mortar', q: 'mortar' }],
  'groundworkers': [{ label: 'Cement', q: 'cement' }, { label: 'Aggregates', q: 'ballast aggregate' }],
  'drainage': [{ label: 'Drainage pipe', q: 'underground drainage pipe' }, { label: 'Fittings', q: 'drainage fittings' }],
  'kitchen-fitters': [{ label: 'Kitchen units', q: 'kitchen units' }, { label: 'Worktops', q: 'kitchen worktop' }],
  'plumbers': [{ label: 'Copper pipe', q: 'copper pipe' }, { label: 'Fittings', q: 'pipe fittings' }],
  'electricians': [{ label: 'Cable', q: 'electrical cable' }, { label: 'Accessories', q: 'electrical accessories' }],
  'plasterers': [{ label: 'Plasterboard', q: 'plasterboard' }, { label: 'Plaster', q: 'plaster' }],
  'painters': [{ label: 'Paint', q: 'paint' }, { label: 'Decorating', q: 'decorating supplies' }],
  'carpenters': [{ label: 'Timber', q: 'timber' }, { label: 'Screws', q: 'wood screws' }],
  'roofers': [{ label: 'Roof tiles', q: 'roof tiles' }, { label: 'Felt', q: 'roofing felt' }],
  'tilers': [{ label: 'Tiles', q: 'wall tiles' }, { label: 'Adhesive', q: 'tile adhesive' }],
  'gardeners': [{ label: 'Topsoil', q: 'topsoil' }, { label: 'Decking', q: 'decking boards' }],
  'general-builders': [{ label: 'Building materials', q: 'building materials' }, { label: 'Tools', q: 'power tools' }],
};

// Specific overrides for high-value calculators.
const SLUG_KEYWORDS = {
  'concrete-volume': [{ label: 'Ready-mix / bags', q: 'ready mix concrete' }, { label: 'Cement', q: 'cement' }],
  'paint-coverage': [{ label: 'Paint', q: 'emulsion paint' }],
  'insulation': [{ label: 'Insulation', q: 'loft insulation' }],
  'decking-boards': [{ label: 'Decking boards', q: 'decking boards' }],
  'fence-panels': [{ label: 'Fence panels', q: 'fence panels' }],
  'paving-slabs': [{ label: 'Paving slabs', q: 'paving slabs' }],
  'block-paving': [{ label: 'Block paving', q: 'block paving' }],
  'topsoil': [{ label: 'Topsoil', q: 'topsoil' }],
  'plasterboard': [{ label: 'Plasterboard', q: 'plasterboard' }],
  'plaster-skim': [{ label: 'Plaster', q: 'multi finish plaster' }],
  'copper-pipe': [{ label: 'Copper pipe', q: 'copper pipe' }],
  'radiator-btu': [{ label: 'Radiators', q: 'radiators' }],
  'boiler-size': [{ label: 'Combi boilers', q: 'combi boiler' }],
  'cable-size': [{ label: 'Cable', q: 'electrical cable' }],
  'roof-tiles': [{ label: 'Roof tiles', q: 'roof tiles' }],
  'roofing-felt': [{ label: 'Roofing felt', q: 'roofing felt' }],
  'tile-quantity': [{ label: 'Tiles', q: 'wall tiles' }, { label: 'Adhesive', q: 'tile adhesive' }],
  'timber-quantity': [{ label: 'Timber', q: 'timber' }],
  'decking-boards': [{ label: 'Decking boards', q: 'decking boards' }],
  'mortar-mix': [{ label: 'Cement', q: 'cement' }, { label: 'Building sand', q: 'building sand' }],
  'bricks-per-area': [{ label: 'Bricks', q: 'facing bricks' }, { label: 'Mortar', q: 'mortar' }],
};

// Returns [{label, q}] for a calculator, or null if we shouldn't show buy links
// (e.g. business/admin calculators).
export function buyKeywordsFor(calc) {
  if (!calc || calc.trade === 'business-essentials') return null;
  return SLUG_KEYWORDS[calc.slug] || TRADE_KEYWORDS[calc.trade] || null;
}
